import { useEffect, useRef } from "react";
import { PENDING_POLL_INTERVAL_MS, shouldPoll } from "./pendingOrderPolling";

interface UsePendingOrderPollingOptions {
  /** 是否正顯示「訂單建立中」空狀態；true 時才啟動背景輪詢。 */
  active: boolean;
  /** 查詢所需的前置條件是否就緒（例如 cusJwtToken），對齊既有查詢的就緒判斷。 */
  ready: boolean;
  /** 是否已有查詢在進行中（含手動按「重新整理」觸發的）；true 時跳過本次 tick，避免重疊請求。 */
  externalInFlight: boolean;
  /** 靜默查一次：由呼叫端決定實際要打哪一支查詢（哨兵路由 vs 真實訂單 id）。 */
  run: () => Promise<void> | void;
}

/**
 * 「訂單建立中」空狀態的背景輪詢。
 *
 * LIFF 網頁收不到 push，瓦斯行後台建單後客戶原本要手動按「重新整理」才會看到最新結果。
 * 這個 hook 在空狀態顯示期間，每 5 秒靜默查一次（不觸發任何可見的 loading／錯誤提示，
 * 那是「重新整理」按鈕自己的事）；分頁切到背景時暫停，切回前景時立即補查一次；
 * 單一工作階段最長跑 10 分鐘；元件卸載、`active`／`ready` 轉為 false 時停止。
 *
 * 決策邏輯（何時該查）抽在 pendingOrderPolling.ts 的純函式 shouldPoll，這裡只負責
 * timer 與 visibilitychange 事件的接線，並把「是否已有查詢在跑」的內部與外部（手動）
 * 狀態合併後交給 shouldPoll 判斷。
 */
export const usePendingOrderPolling = ({ active, ready, externalInFlight, run }: UsePendingOrderPollingOptions): void => {
  // 用 ref 保存最新的 run／externalInFlight，避免它們的變動導致 effect 重跑、interval 被重建
  const runRef = useRef(run);
  runRef.current = run;
  const externalInFlightRef = useRef(externalInFlight);
  externalInFlightRef.current = externalInFlight;
  const internalInFlightRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active || !ready) {
      startedAtRef.current = null;
      return;
    }
    startedAtRef.current = Date.now();

    const tick = () => {
      const ok = shouldPoll({
        visible: document.visibilityState === "visible",
        inFlight: internalInFlightRef.current || externalInFlightRef.current,
        startedAt: startedAtRef.current,
        now: Date.now(),
      });
      if (!ok) return;
      internalInFlightRef.current = true;
      Promise.resolve()
        .then(() => runRef.current())
        .catch(() => {
          // 靜默輪詢失敗不提示；維持空狀態，等下一輪或使用者按「重新整理」
        })
        .finally(() => {
          internalInFlightRef.current = false;
        });
    };

    const intervalId = window.setInterval(tick, PENDING_POLL_INTERVAL_MS);

    // 分頁從背景切回前景：立即補查一次（仍受 shouldPoll 的重疊／時限條件約束）
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      startedAtRef.current = null;
    };
  }, [active, ready]);
};
